import { DEFAULT_REFRESH_INTERVAL } from '../constants'
import { WindowSize, Device } from '../helpers'
import WebModel from '../models/WebModel'
import { Slot } from './Slot'
import SlotRepository from './SlotRepository'

export const ADHESION_HEIGHT = 100
export const STICKY_VIDEO_HEIGHT = 210
const DEFAULT_MIN_WIDTH_FOR_STICKY = 767 //px

export class Sidebar extends Slot {
  offset = 10
  slotName = 'sidebar'

  constructor(
    slotModel: ISlotModel,
    model: WebModel,
    private windowSize = WindowSize,
    private device = Device
  ) {
    super(slotModel, model)
    this._sizes = this.adSizes
    this.setWidthBasedSettings()
    this.setRefreshableSettings()
  }

  validate({ model, windowSize } = this): boolean {
    /** If a breakpoint is configured, use it to validate unit */
    if (model.sidebar_minimum_width) {
      return super.validate() && windowSize.width >= model.sidebar_minimum_width
    }

    return super.validate()
  }

  /**
   * BTF Slots are eligible to be refreshed
   */
  private setRefreshableSettings({ adUnitId } = this) {
    if (adUnitId === 'sidebar_btf') {
      this.refreshTime = DEFAULT_REFRESH_INTERVAL
    }
  }

  /**
   * We don't know when the sidebar will collapse below content
   * unless sidebar_minimum_width set. So we do some safeguards.
   */
  private setWidthBasedSettings({ model } = this) {
    if (model.sidebar_minimum_width) {
      return
    }
    /**
     * We may non-lazy load an ATF ad
     * near the bottom of the page.
     */
    this.lazy = true
    if (this.hasUnsuitableStickyConditions()) {
      this.sticky = false
    }
  }

  /**
   * Pretty high chance we don't want a sticky sidebar ad
   * if the window width is below a specific width or its
   * a tablet or mobile device.
   */
  private hasUnsuitableStickyConditions(
    { windowSize, device } = this
  ): boolean {
    return (
      windowSize.width < DEFAULT_MIN_WIDTH_FOR_STICKY || device.isMobileOrTablet
    )
  }

  private get adSizes(): AdSizes {
    let sizes: AdSizes = [
      [300, 250],
      [300, 600],
      [160, 600],
      [300, 1050],
      [120, 600]
    ]

    if (this.adUnitId === 'sidebar_btf') {
      sizes = this.validateSidebarAdunitSizes(this.model) as AdSizes
    }

    return sizes
  }

  /**
   * This function checks to see if there is enough room on the page to allow for tall
   * sidebar ads.  Must be run after StickyVideo and Adhesions code has run, because
   * it depends on whether those will exist on the page.
   *
   * @param model WebModel
   */
  private validateSidebarAdunitSizes(model: WebModel): AdSizes {
    const totalStickyHeight = this.getTotalStickyHeight(
      model.sidebar_btf_sticky_offset
    )
    const windowHeight = WindowSize.height

    // See if we have enough height, and if so, allow taller 600px ads
    if (windowHeight - totalStickyHeight >= 600) {
      return [
        [300, 250],
        [300, 600],
        [160, 600],
        [120, 600]
      ]
    } else {
      // Only room for 300x250 ads
      return [[300, 250]]
    }
  }
  // tslint:enable no-magic-numbers

  private getTotalStickyHeight(sidebarBtfStickyOffset: number): number {
    let totalStickyHeight = 0

    // Add in sticky sidebar offset from the top of the page
    totalStickyHeight +=
      sidebarBtfStickyOffset === 0 ? 0 : sidebarBtfStickyOffset || 10

    // If adhesion is running, add an extra 110px of required height
    const adhesionSlot = SlotRepository.all().find(
      (slot) => slot.type === 'Adhesion'
    )
    const hasAdhesion = !!(adhesionSlot && adhesionSlot.wrapper)

    totalStickyHeight += hasAdhesion ? ADHESION_HEIGHT : 0

    // If sticky video player is running. Add max player height of 190px plus 20px for padding
    const hasStickyVideo = window.mv_video_exists
    totalStickyHeight += hasStickyVideo ? STICKY_VIDEO_HEIGHT : 0

    return totalStickyHeight
  }
}
